/**
 * Company docname sanitizer.
 *
 * `tenantCompany` must always be a real ERPNext Company DOCNAME (e.g. "CSIDE SCRUB"
 * or "CSIDE SCRUB - 0j85q3282r"), never the bare tenant_id (e.g. "0j85q3282r").
 *
 * The JWT `company` claim (and some login/register responses) historically carried
 * the tenant_id in the `company` field for older sessions. When that tenant_id is
 * sent to any backend endpoint that loads a Company doc
 * (`frappe.has_permission("Company", doc=company, throw=True)`), Frappe raises
 * DoesNotExistError → HTTP 404. This affected get_dashboard_summary and every other
 * company-scoped endpoint (GBP, marketplace, filter attributes, Sales Order company
 * default, item attributes, promotions, StandardDetailPage defaults, ...).
 *
 * A tenant_id is exactly 10 lowercase-alphanumeric characters. A real Company docname
 * is a human-readable name (contains uppercase and/or spaces and/or the " - <id>"
 * suffix) and never matches that shape. So we coerce a bare tenant_id to `null`, which
 * makes consumers fall back to "no company selected" (empty state) instead of sending
 * a wrong docname that 404s. Once the token refreshes with a correct claim, the real
 * docname flows back in.
 */
const BARE_TENANT_ID_RE = /^[a-z0-9]{10}$/;

export function sanitizeCompanyDocname(
  company: string | null | undefined,
  tenantId?: string | null,
): string | null {
  if (!company) return null;
  // Belt-and-suspenders: if the claim literally equals the tenant_id, it's not a docname.
  if (tenantId && company === tenantId) return null;
  // A bare 10-char tenant_id is never a valid Company docname.
  if (BARE_TENANT_ID_RE.test(company)) return null;
  return company;
}
